import { Form } from '@sravni/cosago-react-library/lib/components';
import { useBoolean } from '@sravni/react-utils';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { checkIsShowAccordionBlockEditIcon } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';
import type { IFormHeader } from 'shared/types/IFormHeader';
import { AnketaFormPopup } from 'shared/ui/AnketaFormPopup';
import { CustomAccordionItem } from 'shared/ui/CustomAccordion';

import { selectContactsInfo } from 'src/client/entities/creditForm';

import { ContactsInfoModalTexts } from '../../ContactsInfo.texts';
import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { Form as FormElement } from '../Form/Form';

export type TContactsInfoAccordionProps = TAccordionProps & {
  shouldRestoreAdditionalData?: boolean;
};

export const AccordionModalForm: FC<TContactsInfoAccordionProps> = ({
  isOpen,
  shouldForceOpenPopup = false,
  isFormForceOpened = false,
  onFormSubmit,
  isLoading,
  shouldRestoreAdditionalData,
  isDisableEdit = false,
  shouldScrollIntoAccordion,
  isPopupClosable = false,
  onPopupClose,
  children,
  isSummary,
  esiaLoginBlock,
  stepIndex,
  isHideAccordionData,
  formId,
  icon,
  submitButtonText,
  isHideDivider = false,
  accordionIconSize,
  TitleComponent,
  DescriptionComponent,
  defaultDescription,
  defaultTitle,
  isAlwaysShowDescription = false,
  onPopupOpen,
  isAnyFormStepOpenedInPopup,
  onAuthorizationSuccess,
  analyticsOverrides,
}) => {
  const { data, isFullFilled } = useAppSelector(selectContactsInfo);
  const [shouldShowEditPopup, setShouldShowEditPopup] = useBoolean();
  const [_, setHeader] = useState<IFormHeader>();
  const [title, description] = useAccordionHeaderParams(
    data,
    {
      title: defaultTitle,
      description: defaultDescription,
    },
    isOpen,
    isHideAccordionData,
  );

  const isWithEditItem = checkIsShowAccordionBlockEditIcon(
    isOpen,
    isSummary || isFullFilled,
    isDisableEdit,
    shouldForceOpenPopup,
  );

  const isAccordionOpen = !isFullFilled && isOpen;
  const isEditPopupVisible = shouldShowEditPopup || (shouldForceOpenPopup && isFormForceOpened);
  // Если shouldForceOpenPopup false, то нужно давать возможнсть отредактировать данные и не дизейблить их
  // сделано для логики короткого продления, когда при восстановлении нет валидации
  const isDisabled = !isOpen && !isFullFilled && (shouldForceOpenPopup || isDisableEdit);
  const descriptionText = useMemo(
    () => (!isDisabled || isAlwaysShowDescription ? description : ''),
    [description, isAlwaysShowDescription, isDisabled],
  );
  const isWithDivider = !isSummary && !isHideDivider;

  const onEditButtonClick = useCallback(() => {
    onPopupOpen?.();
    setShouldShowEditPopup.on();
  }, [onPopupOpen, setShouldShowEditPopup]);

  return (
    <>
      <CustomAccordionItem
        onEditButtonClick={onEditButtonClick}
        withDivider={isWithDivider}
        icon={icon}
        iconSize={accordionIconSize}
        isOpen={isAccordionOpen}
        disabled={isDisabled}
        withEdit={isWithEditItem}
        title={TitleComponent ? <TitleComponent disabled={isDisabled}>{title}</TitleComponent> : title}
        description={
          DescriptionComponent ? (
            <DescriptionComponent disabled={isDisabled}>{descriptionText}</DescriptionComponent>
          ) : (
            descriptionText
          )
        }
        scrollIntoView={isAccordionOpen && shouldScrollIntoAccordion}
      >
        {esiaLoginBlock && <Form.Row colWidths={[8]}>{esiaLoginBlock}</Form.Row>}
        <FormElement
          onFormSubmit={onFormSubmit}
          setHeader={setHeader}
          isFormForceOpened={isFormForceOpened}
          isLoading={isLoading}
          shouldRestoreAdditionalData={shouldRestoreAdditionalData}
          stepIndex={stepIndex}
          formId={formId}
          submitButtonText={submitButtonText}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
          onAuthorizationSuccess={onAuthorizationSuccess}
          analyticsOverrides={analyticsOverrides}
        />
      </CustomAccordionItem>

      <AnketaFormPopup
        submitButtonText={submitButtonText}
        isClosable={isPopupClosable}
        isFormForceOpened
        FormElement={FormElement}
        onFormSubmit={onFormSubmit}
        onClose={(isSubmit) => {
          setShouldShowEditPopup.off();
          onPopupClose?.(isSubmit);
        }}
        isLoading={isLoading}
        isOpen={isEditPopupVisible}
        icon={icon}
        iconSize={accordionIconSize}
        title={TitleComponent ? <TitleComponent>{defaultTitle}</TitleComponent> : defaultTitle}
        subtitle={
          DescriptionComponent ? (
            <DescriptionComponent>{ContactsInfoModalTexts.description}</DescriptionComponent>
          ) : (
            ContactsInfoModalTexts.description
          )
        }
        setHeader={setHeader}
        stepIndex={stepIndex}
        onAuthorizationSuccess={onAuthorizationSuccess}
        analyticsOverrides={analyticsOverrides}
      >
        {children}
      </AnketaFormPopup>
    </>
  );
};
